import { Add, Search, TuneOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Drawer,
  InputAdornment,
  Modal,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState, useMemo, useEffect } from "react";
import { useStyle } from "./styles";
import { CardPerfil } from "../../../componentes/easyReport/cardPerfil/Index";
import { RightBoxContent } from "../../../componentes/easyReport/rightBox/Index";
import { NewPerfilModal } from "../../../componentes/easyReport/newPerfilModal/Index";
import AlertModal from "../../../componentes/alertModal";
import { notifyOnPage } from "../../../utils/notifyOnPage";
import api from "../../../services/api";
import { useNavigate } from "react-router-dom";
import usePermissao from "../../../hooks/usePermissao";
import { ReactComponent as EmptyBox } from "../../../assets/easyReport/iconSvg/emptyBox.svg";

export interface Perfil {
  id: string;
  titulo: string;
  isDefault?: boolean;
  descricao: string;
  funcoes: {
    idFuncao: string;
    nomeFuncao: string;
    view: string;
    code: string;
    styles: string;
    variaveis: { variableName: string }[];
  }[];
  agendamentos?: string[];
  relatorios?: string[];
}

export default function PerfilRelatorio() {
  const [perfis, setPerfis] = useState<Perfil[]>([]);
  const [selectedPerfil, setSelectedPerfil] = useState<Perfil | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [idToDelete, setIdToDelete] = useState("");
  const [searchText, setSearchText] = useState("");
  const [warningModal, setWarningModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isLg = useMediaQuery(theme.breakpoints.up("xl"));
  const styles = useStyle(theme);
  const navigate = useNavigate();
  const { canEdit } = usePermissao("easyReport");

  useEffect(() => {
    const fetchPerfil = async () => {
      try {
        setLoading(true);
        const response = await api.get("/easyReport/v2/findManyProfiles");

        setPerfis(response.data);
      } catch (error: any) {
        notifyOnPage(
          "easyReportV2/perfilRelatorios",
          error?.response?.data?.error || "Erro ao carregar perfis",
          "error",
          3000,
        );
      } finally {
        setLoading(false);
      }
    };
    fetchPerfil();
  }, []);

  const handleSelectPerfil = (perfil: Perfil | null) => {
    setSelectedPerfil(perfil);
    setEditMode(false);
    if (isMobile) setDrawerOpen(true);
  };

  const handleEditPerfil = (perfil: Perfil | null) => {
    setSelectedPerfil(perfil);
    setEditMode(true);
    if (isMobile) setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedPerfil(null);
    setEditMode(false);
  };

  const handleDeletePerfil = (
    id: string,
    agendamentos: string[],
    relatorios: string[],
  ) => {
    if (agendamentos.length > 0 || relatorios.length > 0) {
      setWarningModal(true);
      setSelectedPerfil(perfis.find((p) => p.id === id) ?? null);
      return;
    }
    setDeleteModal(true);
    setIdToDelete(id);
  };

  const handleDeleteModal = async () => {
    try {
      const logsString = localStorage.getItem("logs");
      const logsLoginClienteId = logsString ? JSON.parse(logsString)?.id : "";
      await api.post("/easyReport/v2/deleteProfile", {
        id: idToDelete,
        logsLoginClienteId,
      });
      setPerfis((prev) => prev.filter((perfil) => perfil.id !== idToDelete));
      setIdToDelete("");
      setDeleteModal(false);
      notifyOnPage(
        "easyReportV2/perfilRelatorios",
        "Perfil excluído com sucesso!",
        "success",
        3000,
      );
    } catch (error) {
      setDeleteModal(false);
      notifyOnPage(
        "easyReportV2/perfilRelatorios",
        (error as any)?.response?.data?.error || "Erro ao deletar perfil",
        "error",
        3000,
      );
    }
  };

  const filteredPerfis = useMemo(
    () =>
      perfis.filter(
        (perfil) =>
          perfil.titulo.toLowerCase().includes(searchText.toLowerCase()) ||
          perfil.descricao.toLowerCase().includes(searchText.toLowerCase()),
      ),
    [perfis, searchText],
  );
  const obterMensagem = () => {
    const itens = [];
    if (selectedPerfil?.relatorios?.length)
      itens.push(...selectedPerfil.relatorios);
    if (selectedPerfil?.agendamentos?.length)
      itens.push(...selectedPerfil.agendamentos);

    const tipos = [];
    if (selectedPerfil?.relatorios?.length) tipos.push("relatórios");
    if (selectedPerfil?.agendamentos?.length) tipos.push("agendamentos");

    const tiposTexto = tipos.join(" e ");

    return `Não é possível excluir este perfil, pois ele possui ${tiposTexto} vinculados: ${itens.join(", ")}.`;
  };

  return (
    <Box sx={styles.pageContainer}>
      <Box sx={styles.boxTitle}>
        <Typography variant="h6">Perfis de Relatório</Typography>
      </Box>

      <Box sx={styles.container}>
        <Paper sx={styles.paper}>
          {isMobile ? (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <TextField
                size="small"
                value={searchText}
                placeholder="Pesquisar por título ou descrição"
                onChange={(e) => setSearchText(e.target.value)}
                sx={styles.searchInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              {canEdit && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    sx={styles.button}
                    onClick={() => setModalOpen(true)}
                    fullWidth
                  >
                    {"Novo Perfil"}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<TuneOutlined />}
                    sx={styles.button}
                    fullWidth
                    onClick={() =>
                      navigate("/easyReportV2/perfilRelatorios/funcao")
                    }
                  >
                    Funções Personalizadas
                  </Button>
                </>
              )}
            </Box>
          ) : (
            <Box sx={styles.boxSearchAndButton}>
              <TextField
                size="small"
                value={searchText}
                placeholder="Pesquisar por título ou descrição"
                onChange={(e) => setSearchText(e.target.value)}
                sx={styles.searchInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              {canEdit && (
                <Box sx={styles.buttonBox}>
                  <Tooltip title="Funções personalizadas">
                    {isLg ? (
                      <Button
                        variant="outlined"
                        startIcon={<TuneOutlined />}
                        sx={styles.button}
                        onClick={() =>
                          navigate("/easyReportV2/perfilRelatorios/funcao")
                        }
                      >
                        Funções
                      </Button>
                    ) : (
                      <Button
                        onClick={() =>
                          navigate("/easyReportV2/perfilRelatorios/funcao")
                        }
                        sx={styles.buttonMobileOutlined}
                        variant="outlined"
                      >
                        <TuneOutlined />
                      </Button>
                    )}
                  </Tooltip>
                  <Tooltip title="Criar Perfil">
                    {isLg ? (
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        sx={styles.button}
                        onClick={() => setModalOpen(true)}
                      >
                        {"Novo Perfil"}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setModalOpen(true)}
                        sx={styles.buttonMobile}
                        variant="contained"
                      >
                        <Add />
                      </Button>
                    )}
                  </Tooltip>
                </Box>
              )}
            </Box>
          )}
          <Box
            sx={{
              display: "block",
              gap: 1,
            }}
          >
            {loading &&
              Array.from({ length: 4 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  height={80}
                  sx={{ mb: 1 }}
                />
              ))}
            {!loading && filteredPerfis.length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                  p: 2,
                  color: "text.disabled",
                }}
              >
                <EmptyBox
                  style={{
                    width: "140px",
                    height: "140px",
                    fill: "currentColor",
                  }}
                />
                <Typography>Nenhum perfil encontrado</Typography>
              </Box>
            )}
            {!loading &&
              filteredPerfis.map((perfil) => (
                <CardPerfil
                  key={perfil.id}
                  perfil={perfil}
                  handleSelectPerfil={handleSelectPerfil}
                  selectedPerfil={selectedPerfil}
                  handleEditPerfil={handleEditPerfil}
                  handleDelete={handleDeletePerfil}
                />
              ))}
          </Box>
        </Paper>
        {isMobile ? (
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={handleDrawerClose}
            PaperProps={{
              sx: { width: "90vw", p: 2 },
            }}
          >
            <RightBoxContent
              selectedPerfil={selectedPerfil}
              setSelectedPerfil={setSelectedPerfil}
              edit={editMode}
              setEdit={setEditMode}
              setPerfis={setPerfis}
              perfis={perfis}
              handleDrawerClose={handleDrawerClose}
            />
          </Drawer>
        ) : (
          <Box sx={{ width: "100%" }}>
            <RightBoxContent
              selectedPerfil={selectedPerfil}
              setSelectedPerfil={setSelectedPerfil}
              edit={editMode}
              setEdit={setEditMode}
              setPerfis={setPerfis}
              perfis={perfis}
              handleDrawerClose={handleDrawerClose}
            />
          </Box>
        )}
        <AlertModal
          openModal={deleteModal}
          setOpenModal={setDeleteModal}
          severity="delete"
          singleButton={false}
          handleButton={handleDeleteModal}
          textContent={
            "Se prosseguir com a operação, o perfil de relatório será movido para a lixeira e excluído permanentemente após 30 dias."
          }
          handleCancel={() => {
            setDeleteModal(false);
          }}
        />
        <AlertModal
          openModal={warningModal}
          setOpenModal={setWarningModal}
          severity="warning"
          singleButton={true}
          buttonText={"Entendi"}
          handleButton={() => setWarningModal(false)}
          textContent={obterMensagem()}
          handleCancel={() => {
            setWarningModal(false);
          }}
        />
        <Modal open={modalOpen} onClose={setModalOpen}>
          <NewPerfilModal
            setPerfis={setPerfis}
            setModalOpen={setModalOpen}
            perfis={perfis}
          />
        </Modal>
      </Box>
    </Box>
  );
}
